import {
  SUPPLIER_MACRO_CATEGORIES,
  supplierMacroByCode,
  supplierMacroOfDetail,
} from '@console/shared-kernel';
import {
  Bed,
  BedDouble,
  Bike,
  Briefcase,
  Building2,
  Car,
  CarFront,
  CarTaxiFront,
  Cloud,
  Coffee,
  Fuel,
  Gift,
  GraduationCap,
  House,
  Landmark,
  MapPin,
  Megaphone,
  Milestone,
  Package,
  PartyPopper,
  Phone,
  Plane,
  Printer,
  Shield,
  ShoppingBag,
  SquareParking,
  TrainFront,
  TramFront,
  UtensilsCrossed,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { ChoiceSelect } from '@/components/ui/choice-select';
import type { components } from '@/lib/api-types-expenses-suppliers';

/**
 * «Fornitori»'s supplier types on two levels (#983, decision 255): a macro-category, derived from
 * the expense type, and optionally one of its details. The catalog is shared-kernel's
 * `SUPPLIER_MACRO_CATEGORIES`, the labels the locales' `type.<code>`.
 */

export type SupplierMacro = components['schemas']['SupplierMacroCategory'];
export type SupplierDetail = components['schemas']['SupplierTypeDetail'];
export type SupplierType = SupplierMacro | SupplierDetail;
export type SupplierTypes = components['schemas']['SupplierTypes'];

export const SUPPLIER_MACROS = SUPPLIER_MACRO_CATEGORIES.map(
  (macro) => macro.code,
) as readonly SupplierMacro[];

/** A macro-category's details, in the catalog's order; none for an unknown one. */
export function detailsOf(macro: string | null): readonly SupplierDetail[] {
  if (macro === null) return [];
  return (supplierMacroByCode(macro)?.details.map((detail) => detail.code) ??
    []) as readonly SupplierDetail[];
}

/** The macro-category a detail belongs to. */
export function macroOf(detail: string): SupplierMacro | null {
  return (supplierMacroOfDetail(detail)?.code ?? null) as SupplierMacro | null;
}

/** Every type of either level, each macro-category followed by its details: the list's filter. */
export const SUPPLIER_TYPES: readonly SupplierType[] = SUPPLIER_MACROS.flatMap((macro) => [
  macro,
  ...detailsOf(macro),
]);

/** One glyph per type, either level: the page, the list, the picker and the map use the same. */
export const TYPE_GLYPH: Record<SupplierType, LucideIcon> = {
  transportation: TrainFront,
  flight: Plane,
  train: TrainFront,
  taxi: CarTaxiFront,
  metro: TramFront,
  ncc: Car,
  scooter: Bike,
  car_rental: CarFront,
  lodging: Bed,
  hotel: BedDouble,
  b_and_b: House,
  apartment: Building2,
  food: UtensilsCrossed,
  restaurant: UtensilsCrossed,
  bar: Coffee,
  fuel: Fuel,
  parking: SquareParking,
  'road-charges': Milestone,
  entertainment: PartyPopper,
  supplies: Package,
  copy_shop: Printer,
  shop: ShoppingBag,
  equipment: Wrench,
  'professional-services': Briefcase,
  telecommunications: Phone,
  gifts: Gift,
  'digital-services': Cloud,
  training: GraduationCap,
  marketing: Megaphone,
  infrastructure: Building2,
  'regulatory-fees': Landmark,
  insurance: Shield,
  'vehicle-expenses': Car,
  other: MapPin,
};

export function glyphOf(type: string | null | undefined): LucideIcon {
  return (type ? TYPE_GLYPH[type as SupplierType] : undefined) ?? MapPin;
}

/** «Trasporto · Taxi», or the macro-category alone. */
export function useSiteTypeLabel(): (macro: string | null, detail: string | null) => string | null {
  const { t } = useTranslation('suppliers');
  return (macro, detail) => {
    if (macro === null) return null;
    const label = t(`type.${macro}` as 'type.other');
    return detail === null ? label : `${label} · ${t(`type.${detail}` as 'type.other')}`;
  };
}

/** A supplier's types as chips' text: each macro-category, with its details after it. */
export function typeChips(
  types: SupplierTypes,
): { readonly code: SupplierType; readonly details: readonly SupplierDetail[] }[] {
  return types.macros.map((macro) => ({
    code: macro,
    details: types.details.filter((detail) => macroOf(detail) === macro),
  }));
}

/** A supplier's types, each macro-category with its details: «Ristorazione (Bar), Carburante». */
export function SupplierTypeList({ types }: { readonly types: SupplierTypes }) {
  const { t } = useTranslation('suppliers');
  if (types.macros.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="flex flex-wrap gap-x-3 gap-y-1">
      {typeChips(types).map(({ code, details }) => {
        // The detail's own glyph when it is the only one (a taxi company shows a taxi).
        const Glyph = glyphOf(details.length === 1 ? details[0] : code);
        return (
          <span key={code} className="inline-flex items-center gap-1 whitespace-nowrap">
            <Glyph aria-hidden="true" className="text-muted-foreground size-3.5" />
            {t(`type.${code}` as 'type.other')}
            {details.length > 0
              ? ` (${details.map((detail) => t(`type.${detail}` as 'type.other')).join(', ')})`
              : null}
          </span>
        );
      })}
    </span>
  );
}

export type SiteTypeValue = {
  readonly macro: SupplierMacro | '';
  readonly detail: SupplierDetail | '';
};

/** The value a site's form starts from. */
export function siteTypeValueOf(
  site: { readonly macro: SupplierMacro | null; readonly detail: SupplierDetail | null } | null,
): SiteTypeValue {
  return { macro: site?.macro ?? '', detail: site?.detail ?? '' };
}

/**
 * «Tipo» and «Dettaglio» (#983): the macro-category, then one of its details when it has some. A
 * new macro-category clears a detail that is not its own. `field` lays each out as its form does.
 */
export function SiteTypeFields({
  value,
  onChange,
  field,
  triggerClassName,
}: {
  readonly value: SiteTypeValue;
  /** The selects' trigger, as the form's other fields. */
  readonly triggerClassName?: string;
  readonly onChange: (value: SiteTypeValue) => void;
  readonly field: (
    key: 'macro' | 'detail',
    label: string,
    control: (id: string) => ReactNode,
  ) => ReactNode;
}) {
  const { t } = useTranslation('suppliers');
  const details = detailsOf(value.macro || null);
  const macros = [
    { value: '', label: t('siteType.none') },
    ...SUPPLIER_MACROS.map((code) => ({ value: code, label: t(`type.${code}` as 'type.other') })),
  ];
  const detailChoices = [
    { value: '', label: t('siteType.noDetail') },
    ...details.map((code) => ({ value: code, label: t(`type.${code}` as 'type.other') })),
  ];
  return (
    <>
      {field('macro', t('siteType.macro'), (id) => (
        <ChoiceSelect
          id={id}
          {...(triggerClassName ? { triggerClassName } : {})}
          value={value.macro}
          choices={macros}
          onChange={(next) => {
            const macro = next as SupplierMacro | '';
            const keep = value.detail !== '' && macroOf(value.detail) === macro;
            onChange({ macro, detail: keep ? value.detail : '' });
          }}
        />
      ))}
      {details.length > 0
        ? field('detail', t('siteType.detail'), (id) => (
            <ChoiceSelect
              id={id}
              {...(triggerClassName ? { triggerClassName } : {})}
              value={value.detail}
              choices={detailChoices}
              onChange={(next) => onChange({ ...value, detail: next as SupplierDetail | '' })}
            />
          ))
        : null}
    </>
  );
}

/**
 * «Tipi del fornitore» (#983): its own macro-categories, proposed from its expenses and editable by
 * who may complete it. Its sites' types are shown apart: they come from the sites.
 */
export function MacroCategoriesField({
  value,
  onChange,
  fromSites,
}: {
  readonly value: readonly SupplierMacro[];
  readonly onChange: (value: readonly SupplierMacro[]) => void;
  /** The macro-categories its active sites give, not editable here. */
  readonly fromSites: readonly SupplierMacro[];
}) {
  const { t } = useTranslation('suppliers');
  const toggle = (code: SupplierMacro, on: boolean) =>
    onChange(SUPPLIER_MACROS.filter((m) => (m === code ? on : value.includes(m))));
  const extra = fromSites.filter((code) => !value.includes(code));
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="text-sm font-semibold">
        {t('types.own')}
        <span className="text-muted-foreground font-normal"> {t('edit.optionalMasculine')}</span>
      </legend>
      <span className="text-muted-foreground text-xs">{t('types.ownHelp')}</span>
      <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
        {SUPPLIER_MACROS.map((code) => (
          <label key={code} className="flex min-h-11 cursor-pointer items-center gap-3 text-sm">
            <Checkbox
              checked={value.includes(code)}
              onChange={(event) => toggle(code, event.target.checked)}
            />
            {t(`type.${code}` as 'type.other')}
          </label>
        ))}
      </div>
      {extra.length > 0 ? (
        <span className="text-muted-foreground text-xs">
          {t('types.fromSites', {
            types: extra.map((code) => t(`type.${code}` as 'type.other')).join(', '),
          })}
        </span>
      ) : null}
    </fieldset>
  );
}
